import { CheckCircleIcon } from "@heroicons/react/24/outline"
import { LoversApi } from "api/LoversApi"
import { useState } from "react"
import { TimelineComponent } from "./TimelineComponent"
import dayjs from "dayjs"
import { useQueryClient } from "@tanstack/react-query"

type Props = {
  id: string
  name: string
  slug?: string
  opensAt?: string
  creatorName: string
  createdAt: string
  entries?: {
    creatorName: string
    timestamp: string
  }[]
}

export const NewTimeCapsuleEntryForm = (props: Props) => {
  const [entryText, setEntryText] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [isSaved, setIsSaved] = useState(false)
  const queryClient = useQueryClient()

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    setIsLoading(true)
    LoversApi()
      .timeCapsule.createTimeCapsuleEntry({ timeCapsuleId: props.id, text: entryText })
      .then(() => {
        setIsSaved(true)
        queryClient.invalidateQueries({ queryKey: ["useTimeCapsuleBySlug", props.slug] })
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const resetForm = () => {
    setEntryText("")
    setIsSaved(false)
  }

  if (isSaved) {
    return (
      <div className="bg-white rounded-lg p-8 text-center">
        <CheckCircleIcon
          className="mx-auto h-12 w-12 text-green-500"
          aria-hidden="true"
        />
        <h3 className="mt-2 text-2xl font-bold text-gray-900">Entry Saved</h3>
        <p className="mt-1 text-sm text-gray-500">
          {`Your entry has been saved to ${props.name}.`}
        </p>
        <div className="mt-6">
          <button
            type="button"
            onClick={resetForm}
            className="mt-2 lg:w-auto w-full justify-center inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Write New Entry
          </button>
        </div>
      </div>
    )
  }

  return props.entries?.length === 0 ? (
    <FirstEntryForm
      handleSubmit={handleSubmit}
      entryText={entryText}
      setEntryText={setEntryText}
      isLoading={isLoading}
    />
  ) : (
    <ExistingEntryForm
      handleSubmit={handleSubmit}
      entryText={entryText}
      setEntryText={setEntryText}
      isLoading={isLoading}
      {...props}
    />
  )
}

type FormProps = {
  handleSubmit: (e: React.FormEvent) => void
  entryText: string
  setEntryText: (text: string) => void
  isLoading: boolean
}

const FirstEntryForm = ({
  handleSubmit,
  entryText,
  setEntryText,
  isLoading
}: FormProps) => {
  return (
    <>
      <form onSubmit={handleSubmit}>
        <label htmlFor="entry" className="block text-sm/6 font-medium text-gray-900">
          Create a first entry
        </label>
        <div className="mt-2">
          <textarea
            id="entry"
            name="entry"
            rows={8}
            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm/6"
            value={entryText}
            placeholder="Say something to the future..."
            onChange={(e) => setEntryText(e.target.value)}
          />
        </div>
        <button
          type="submit"
          disabled={isLoading || !entryText}
          className="mt-2 lg:w-auto w-full justify-center lg:float-right inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:hover:bg-primary-600"
        >
          {isLoading ? "Saving..." : "Save entry"}
        </button>
      </form>
    </>
  )
}

const ExistingEntryForm = ({
  handleSubmit,
  entryText,
  setEntryText,
  isLoading,
  ...props
}: FormProps & Props) => {
  const MAX_DISPLAY_ENTRIES = 7

  const timeline =
    props.entries && props.entries?.length > MAX_DISPLAY_ENTRIES
      ? [
          {
            id: "more-entries",
            title: `...${props.entries.length - MAX_DISPLAY_ENTRIES} other entries`,
            text: "",
            timestamp: "",
            type: "simple"
          } as const,
          ...props.entries.slice(-MAX_DISPLAY_ENTRIES).map(
            (entry) =>
              ({
                id: entry.timestamp,
                title: entry.creatorName,
                text: "added an entry",
                timestamp: dayjs(entry.timestamp).format("ddd HH:mm - MMM D, YYYY"),
                type: "simple"
              }) as const
          )
        ]
      : props.entries?.map(
          (entry) =>
            ({
              id: entry.timestamp,
              title: entry.creatorName,
              text: "added an entry",
              timestamp: dayjs(entry.timestamp).format("ddd HH:mm - MMM D, YYYY"),
              type: "simple"
            }) as const
        ) || []

  return (
    <>
      <TimelineComponent
        entries={[
          {
            id: "created",
            title: "Capsule created",
            text: "",
            timestamp: dayjs(props.createdAt).format("ddd HH:mm - MMM D, YYYY"),
            type: "simple"
          },
          ...timeline
        ]}
      />
      <div className="mt-6 flex gap-x-3">
        <form onSubmit={handleSubmit} className="relative flex-auto">
          <div className="overflow-hidden rounded-lg pb-12 shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-primary-600">
            <label htmlFor="entry" className="sr-only">
              Add an entry
            </label>
            <textarea
              id="entry"
              name="entry"
              rows={6}
              placeholder="Add a new entry..."
              className="block w-full resize-none border-0 bg-transparent py-1.5 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm/6"
              value={entryText}
              onChange={(e) => setEntryText(e.target.value)}
            />
          </div>

          <div className="absolute inset-x-0 bottom-0 flex justify-end py-2 pl-3 pr-2">
            <button
              type="submit"
              disabled={isLoading || !entryText}
              className="mt-2 lg:w-auto w-full justify-center lg:float-right inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:hover:bg-primary-600"
            >
              {isLoading ? "Saving..." : "Save entry"}
            </button>
          </div>
        </form>
      </div>
    </>
  )
}
